import React, { useState } from "react";
import Cookies from "universal-cookie";

import ProductService from "../services/ProductService";
import StrapsService from "../services/StrapsService";
import DicesService from "../services/DicesService";
import CookieService from "../services/CookieService";
import { ItemSize } from "../services/ItemSize";
import CurrencyFormat from "../services/CurrencyService";

import { useIsMobile } from "../hooks/useIsMobile";

var timerAlert = null;

const Context = React.createContext();

const useMainContext = () => {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error("Context error");
  }
  return context;
};

const cookies = new Cookies();

const Provider = ({ children }) => {
  const [state, setState] = useState({
    diceSelected: null,
    dices: DicesService.getDices(),
    straps: StrapsService.getStraps(),
    product: ProductService.getProduct(),
    dicesDic: DicesService.getDicesDic(),
    menuItem: "strap",
    showAlert: false,
    alertMessage: "",
    strapType: "Strap",
  });

  const { isMobile } = useIsMobile();

  const getDicesIds = (dices) => {
    let ids = [];
    dices.forEach((dice, index) => {
      if (!dice.is_anagram) {
        ids.push(dice.id);
      } else {
        ids.push("anagram");
      }
    });
    return ids;
  };

  const setDices = (product) => {
    setState({
      ...state,
      product,
    });
    CookieService.setCookie("dices", getDicesIds(product.dices));
  };

  const setStrapCookie = (strap) => {
    CookieService.setCookie("strap", strap.id);
  };

  const setTutorialCookie = () => {
    CookieService.setCookie("tutorial", true);
  };

  const getWidth = (target) => {
    return !isMobile
      ? `${target}px`
      : `${(100 / window.innerWidth) * target}vw`;
  };

  return (
    <Context.Provider
      value={{
        diceSelected: state.diceSelected,
        dices: state.dices,
        straps: state.straps,
        strapType: state.strapType,
        product: state.product,
        isMobile: isMobile,
        menuItem: state.menuItem,
        showAlert: state.showAlert,
        alertMessage: state.alertMessage,
        strapPosition: state.strapPosition,
        isSmallMobile: state.isSmallMobile,
        getDice: (dice) => {
          setState({
            ...state,
            diceSelected: dice,
          });
        },
        addDice: (dice) => {
          let product = { ...state.product };
          product.dices.push(dice);
          setDices(product);
        },
        setSuggestion: (dices, strapId) => {
          let product = { ...state.product };
          product.strap = state.straps.find((item) => item.id === strapId);
          const newDices = [...dices.map((dice) => {
            if (dice.id) {
              return state.dicesDic[dice.id]
            }
            return dice;
          })];
          product.dices = newDices;
          setDices(product);
        },
        reorderDices: (dicesReordered) => {
          let product = { ...state.product };
          product.dices = dicesReordered;
          setDices(product);
        },
        removeDice: (index) => {
          let product = { ...state.product };
          if (index !== -1) {
            product.dices.splice(index, 1);
            setDices(product);
          }
        },
        setStrap: (strapId) => {
          console.log('setStrap', strapId);
          let product = { ...state.product };
          let anagramIndex = product.dices.findIndex((item) => item.is_anagram);
          product.strap = state.straps.find((item) => item.id === strapId);
          product.dices[anagramIndex] = product.strap.anagram;
          setDices(product);
          setStrapCookie(product.strap);
        },
        setProduct: (newProduct) => {
          let product = { ...state.product };
          product.strap = newProduct.strap;
          product.dices = newProduct.dices;
          setDices(product);
          setStrapCookie(product.strap);
        },
        setMenuItem: (item) => {
          setState({
            ...state,
            menuItem: item,
          });
          if (item === "dices" && !cookies.get("tutorial")) {
            setTutorialCookie();
          }
        },
        setStrapType: (type) => {
          setState({ ...state, strapType: type });
        },
        setShowAlert: (alert) => {
          let text =
            ["Alert.dices", "Alert.anagram", "Alert.leatherletter"][alert] ||
            "Alert.dices";

          clearTimeout(timerAlert);
          setState({
            ...state,
            showAlert: true,
            alertMessage: text,
          });
          timerAlert = setTimeout(() => {
            setState({
              ...state,
              showAlert: false,
              alertMessage: text,
            });
          }, 4000);
        },
        setTutorialStep: () => {
          setState({ ...state, menuItem: "dices" });
          setTutorialCookie();
        },
        getDicesHeight: (offset = 0) => {
          let height = isMobile ? -120 : -140;
          state.product.dices.forEach((dice, index) => {
            height += ItemSize.getItemSize(dice, isMobile, true).height;
          });
          if (isMobile && state.product.dices.length >= 5) {
            height -= 30;
          }
          height += offset;
          return getWidth(height);
        },
        getHeightStrap: () => {
          let height = isMobile ? 200 : 300;
          state.product.dices.forEach((dice, index) => {
            height += ItemSize.getItemSize(dice, isMobile, true).height;
          });
          return getWidth(height);
        },
        getItemHeight: (dice) => {
          return ItemSize.getItemSize(dice, isMobile, true).height;
        },
        getTotal: () =>
          CurrencyFormat.calculateFormat(
            (state.product.dices || []).reduce(
              (total, dice) => (dice.is_anagram ? total : total + dice.price),
              state.product.strap.price || 0
            )
          ),
        getProductsIds: () => {
          return (state.product.dices || [])
          .reduce(
            (ids, dice) => {
              !dice.is_anagram && ids.push(dice.id);
              return ids;
            },
            [state.product.strap.id]
          )
          .join()
        },
        getDicesFromCategory: (category) =>
          state.dices.find((item) => item.category === category).dices,
        getProductDicesNumbner: () => state.product.dices.length || 0,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export { Provider, useMainContext, Context };
